import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import type {
  Activity,
  MessageActivity as MessageActivityInterface,
  MessageActivityDetails,
  User
} from '@paintscout/api'
import React, { useContext } from 'react'
import DeleteIcon from '@material-ui/icons/Delete'
import { removeAuth0Prefix } from '@paintscout/util'
import { getMessageSenderDisplayName } from '@paintscout/util/builder'
import linkify from 'linkifyjs/html'
import Highlight from '@paintscout/ui/components/Highlight'
import HtmlContent from '@paintscout/ui/components/HtmlContent'
import { ConfirmationDialog, useTranslation } from '@paintscout/ui'
import { useUser } from '../../../providers/UserProvider'
import { UsersContext } from '../../../providers/UsersProvider/UsersContext'
import type { ActivityTypeComponentProps } from '../Activity'
import ActivityContent from '../ActivityContent'
import ActivityFooter from '../ActivityFooter'
import ActivityTitle from '../ActivityTitle'
import ActivityActions, { ActivityAction } from '../ActivityActions'
import { useDialogs } from '../../../providers/DialogStack'
import QuoteActivitySubtitle from '../QuoteActivitySubtitle'

const useStyles = makeStyles<Theme, { isOwnMessage: boolean; deleted?: boolean; isQuote?: boolean }>((theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    paddingLeft: 0,
    paddingRight: 0,
    maxWidth: ({ isQuote }) => (isQuote ? '230px' : 'inherit'),
    overflowWrap: 'break-word',
    [theme.breakpoints.up('xl')]: {
      maxWidth: ({ isQuote }) => (isQuote ? '240px' : 'inherit')
    }
  },
  message: {
    whiteSpace: 'pre-wrap'
  },
  bubble: ({ isOwnMessage, deleted }) => ({
    background: isOwnMessage && !deleted ? theme.paintscout.palette.brand?.Neutral?.P90 : null,
    color: null,
    '& a': {
      color: 'inherit'
    },
    // Needed in case people have applied custom colors to their templates
    '& span': {
      color: isOwnMessage && !deleted ? `${theme.palette.primary.contrastText} !important` : null
    }
  })
}))

export default function MessageActivity(props: ActivityTypeComponentProps<MessageActivityDetails>) {
  const { user } = useUser()
  const { __ } = useTranslation()
  const { activity, showDocTypeDetails, showContent, actions, onAction, isQuote } = props
  const { message } = activity.details
  const { from, text, deleted } = message

  // `from.name` is the sender's name formatted once at send time, so a seat rename leaves every
  // past message showing the previous person (#21800). Resolve it live, falling back to the
  // snapshot for customers and for users absent from the map (deactivated, or customer-view where
  // the users query returns nothing). Read the context directly — useUsers() throws without a
  // provider, and this variant renders in trees that may not mount one.
  const usersById = useContext(UsersContext)?.usersById
  const senderName = getMessageSenderDisplayName({ from, users: usersById })

  const { openDialog, dismissDialog } = useDialogs()

  const isOwnMessage = checkIsOwnMessage(activity, user)
  const classes = useStyles({ isOwnMessage, deleted, isQuote })

  if (deleted) {
    return (
      <ActivityContent
        classes={{
          root: classes.content,
          bubble: classes.bubble
        }}
      >
        <div>
          <em>{__('This message has been deleted')}</em>
        </div>
      </ActivityContent>
    )
  }

  // Remove `cid:` references, mailto links, and other other malformed tags
  let sanitizedContent = (text || '')
    .replaceAll(/\[cid:[^\]]+\]\n?/g, '')
    .replaceAll(/<mailto:[^>]+>\n?/g, '')
    .replaceAll(/<https:[^>]+>\n?/g, '')
    .replaceAll(/\[image:[^\]]+\]\n?/gi, '')

  // Remove trailing newlines and whitespace
  sanitizedContent = sanitizedContent.replaceAll(/\s+$/g, '')

  let linkifiedContent = sanitizedContent

  try {
    linkifiedContent = linkify(sanitizedContent, {
      defaultProtocol: 'https'
    })
  } catch (error) {
    console.error('Error linkifying content', error)
    linkifiedContent = sanitizedContent
  }

  return (
    <>
      <ActivityTitle
        rightContent={
          actions &&
          (isOwnMessage ? (
            <ActivityActions>
              <ActivityAction
                onClick={() => {
                  openDialog(ConfirmationDialog, {
                    title: __('Are you sure?'),
                    message: __('This will permanently delete the message from the chat'),
                    onConfirm: () => {
                      onAction('delete', activity)
                      dismissDialog()
                    },
                    onCancel: () => dismissDialog()
                  })
                }}
                icon={DeleteIcon}
              >
                {__('Delete')}
              </ActivityAction>
            </ActivityActions>
          ) : (
            <div style={{ width: 30, height: 36 }} aria-hidden="true" />
          ))
        }
      >
        {showDocTypeDetails ? (
          <>
            <Highlight>{senderName}</Highlight> {__('sent a')} <Highlight>{__('message')}</Highlight>
          </>
        ) : (
          <>
            <Highlight>{isOwnMessage ? __('You') : senderName}</Highlight> {__('said:')}
          </>
        )}
      </ActivityTitle>
      {showContent && (
        <ActivityContent
          classes={{
            root: classes.content,
            bubble: classes.bubble
          }}
        >
          <div className={classes.message}>
            <HtmlContent content={linkifiedContent} preWrap />
          </div>
        </ActivityContent>
      )}
      <QuoteActivitySubtitle activity={activity} showDocTypeDetails={showDocTypeDetails} />
      <ActivityFooter activity={activity} timestamp={activity.details.timestamp} />
    </>
  )
}

function checkIsOwnMessage(activity: Activity, user?: User) {
  if (isMessageActivity(activity)) {
    // if there's no user context, then it's being viewed as the customer
    const userIsCustomer = !user

    return (
      (activity.details.message.from.isCustomer && userIsCustomer) ||
      removeAuth0Prefix(user?.user_id) === removeAuth0Prefix(activity.details.message.from.userId)
    )
  }

  return false
}

function isMessageActivity(activity: Activity): activity is MessageActivityInterface {
  return activity.details.activityType === 'message'
}
